import { Button } from '@/components/Button'
import { Tag } from '@/components/Tag'
import { Link } from '@/components/Link'
import { ContactLink } from '@/components/ContactLink'
import { Tooltip } from '@/components/Tooltip'

export type UnitListButtonPricing = 'flat' | 'revenue-management' | 'hidden'

export interface UnitListItem {
	unitNumber: string
	floor?: number
	availableDate?: string
	/** Replaces the available date with custom text (e.g. on-notice masking). */
	availabilityOverride?: string
	priceRange?: string
	baseRent?: string
	leaseTerm?: string
	hasSpecial?: boolean
	/** Custom tag labels rendered in the tags container. */
	tags?: string[]
	/** "Application Available {date}" tag shown for on-notice units. */
	onNoticeTag?: string
	/** Per-unit pricing mode override (e.g. on-notice units force 'hidden'). */
	pricing?: UnitListButtonPricing
}

export interface UnitListButtonProps extends UnitListItem {
	/** Pricing display mode. @default "flat" */
	pricing?: UnitListButtonPricing
	/** Whether fees are included in the total price. Changes pricing layout. @default true */
	hasFees?: boolean
	/** Show the disclaimer asterisk in pricing. @default true */
	hasDisclaimerSymbol?: boolean
	onClick?: () => void
	onPricingClick?: () => void
	onLeaseTermClick?: () => void
	className?: string
}

export function UnitListButton ({
	unitNumber,
	floor,
	availableDate,
	availabilityOverride,
	priceRange,
	baseRent,
	leaseTerm,
	hasSpecial,
	tags,
	onNoticeTag,
	pricing = 'flat',
	hasFees = true,
	hasDisclaimerSymbol = true,
	onClick,
	onPricingClick,
	onLeaseTermClick,
	className = ''
}: UnitListButtonProps) {
	const disclaimer = hasDisclaimerSymbol ? '*' : ''
	const showCalculator = hasFees && pricing !== 'hidden' && onPricingClick
	const hasTags = hasSpecial || !!onNoticeTag || (tags != null && tags.length > 0)

	return (
		<div
			tabIndex={onClick ? 0 : undefined}
			onClick={onClick}
			onKeyDown={e => {
				// Ignore keys bubbling from nested controls (calculator, lease term link)
				if (e.target !== e.currentTarget) return
				if ((e.key === 'Enter' || e.key === ' ') && onClick) {
					e.preventDefault()
					onClick()
				}
			}}
			className={[
				'spaces-unit-list-item spaces:flex spaces:flex-col spaces:gap-8 spaces:w-full spaces:rounded-12 spaces:bg-canvas-base spaces:hover:bg-canvas-4 spaces:transition-colors spaces:cursor-pointer spaces:text-left',
				'spaces:outline-none spaces:focus-visible:ring-2 spaces:focus-visible:ring-brand-base spaces:focus-visible:ring-offset-2',
				'spaces:p-12 spaces:md:p-16',
				className
			].join(' ')}
		>
			{/* Tags */}
			{hasTags && (
				<div className='spaces-unit-list-item-tags spaces:flex spaces:flex-wrap spaces:gap-8 spaces:items-start spaces:w-full'>
					{onNoticeTag && (
						<Tag
							label={onNoticeTag}
							color='brand'
							appearance='subtle'
							size='sm'
						/>
					)}
					{hasSpecial && (
						<Tag
							label='Special'
							color='success'
							appearance='subtle'
							size='sm'
							iconLeft='Tag'
						/>
					)}
					{tags?.map(tag => (
						<Tag
							key={tag}
							label={tag}
							color='content2'
							appearance='stroke'
							size='sm'
						/>
					))}
				</div>
			)}

			{/* Content: overview + pricing */}
			<div
				className={[
					'spaces:flex spaces:w-full',
					'spaces:flex-col spaces:gap-8 spaces:items-start',
					'spaces:md:flex-row spaces:md:gap-8 spaces:md:items-center'
				].join(' ')}
			>
				{/* Overview: unit info */}
				<div
					className={[
						'spaces:flex spaces:w-full spaces:items-start',
						'spaces:md:flex-1 spaces:md:min-w-0 spaces:md:w-auto',
						!hasFees ? 'spaces:md:items-center' : ''
					].join(' ')}
				>
					<div className='spaces:flex spaces:flex-1 spaces:flex-col spaces:min-w-0'>
						<span className='spaces:text-16/24 spaces:font-default spaces:text-content-1'>
							Unit {unitNumber}
						</span>
						{(floor != null || availableDate || availabilityOverride) && (
							<div className='spaces:flex spaces:items-center spaces:gap-8 spaces:text-14/16 spaces:font-default spaces:text-content-3'>
								{floor != null && <span>Floor {floor}</span>}
								{floor != null && (availableDate || availabilityOverride) && (
									<span className='spaces:w-4 spaces:h-4 spaces:rounded-full spaces:bg-canvas-2 spaces:shrink-0' />
								)}
								{availableDate && <span>Avail. {availableDate}</span>}
								{!availableDate && availabilityOverride && <span>{availabilityOverride}</span>}
							</div>
						)}
					</div>
				</div>

				{/* Pricing section */}
				<div
					className={[
						'spaces-unit-list-item-pricing',
						'spaces:flex spaces:items-center spaces:w-full',
						'spaces:md:flex-1 spaces:md:min-w-0 spaces:md:w-auto',
						pricing === 'hidden' ? 'spaces:gap-4' : 'spaces:gap-8',
						!hasFees || pricing === 'hidden' ? 'spaces:md:justify-end' : ''
					].join(' ')}
				>
					{pricing === 'hidden' ? (
						<ContactLink postText='for Pricing' />
					) : !hasFees ? (
						<NoFeesPricing
							baseRent={baseRent}
							leaseTerm={leaseTerm}
							disclaimer={disclaimer}
							pricing={pricing}
							onLeaseTermClick={onLeaseTermClick}
						/>
					) : (
						<>
							<FeesPricing
								priceRange={priceRange}
								baseRent={baseRent}
								leaseTerm={leaseTerm}
								disclaimer={disclaimer}
								pricing={pricing}
								onLeaseTermClick={onLeaseTermClick}
							/>
							{showCalculator && (
								<div className='spaces:hidden spaces:md:block spaces:shrink-0'>
									<Tooltip label='Calculate My Cost' placement='left'>
										<Button
											iconOnly='Calculator'
											appearance='ghost'
											color='content4'
											size='md'
											aria-label='Calculate Cost'
											onClick={e => {
												e.stopPropagation()
												onPricingClick()
											}}
										/>
									</Tooltip>
								</div>
							)}
						</>
					)}
				</div>
			</div>
		</div>
	)
}

/* ── Pricing sub-components ─────────────────────────────────── */

function NoFeesPricing ({
	baseRent,
	leaseTerm,
	disclaimer,
	pricing,
	onLeaseTermClick
}: {
	baseRent?: string
	leaseTerm?: string
	disclaimer: string
	pricing: UnitListButtonPricing
	onLeaseTermClick?: () => void
}) {
	return (
		<div className='spaces:@container spaces:flex spaces:flex-1 spaces:min-w-0 spaces:flex-col spaces:gap-4 spaces:md:items-end spaces:md:justify-end spaces:@pricing-primary:flex-row spaces:@pricing-primary:gap-8'>
			<div className='spaces:flex spaces:items-baseline spaces:whitespace-nowrap'>
				<span className='spaces:text-18/24 spaces:font-strong spaces:text-content-1'>{baseRent}</span>
				<span className='spaces:text-14/16 spaces:font-default spaces:text-content-3'>
					{`\u00A0Base Rent${disclaimer}`}
				</span>
			</div>
			{leaseTerm && (
				<>
					<span className='spaces:hidden spaces:@pricing-primary:block spaces:self-center spaces:w-4 spaces:h-4 spaces:rounded-full spaces:bg-canvas-2 spaces:shrink-0' />
					{pricing === 'revenue-management' && onLeaseTermClick ? (
						<span className='spaces:text-16/24 spaces:font-default spaces:whitespace-nowrap'>
							<Link
								onClick={e => {
									e.stopPropagation()
									onLeaseTermClick()
								}}
								label={leaseTerm}
								defaultUnderline={false}
								aria-label='Change lease term'
								role='button'
							/>
						</span>
					) : (
						<span className='spaces:text-16/24 spaces:font-default spaces:text-content-3 spaces:whitespace-nowrap'>
							{leaseTerm}
						</span>
					)}
				</>
			)}
		</div>
	)
}

function FeesPricing ({
	priceRange,
	baseRent,
	leaseTerm,
	disclaimer,
	pricing,
	onLeaseTermClick
}: {
	priceRange?: string
	baseRent?: string
	leaseTerm?: string
	disclaimer: string
	pricing: UnitListButtonPricing
	onLeaseTermClick?: () => void
}) {
	return (
		<div className='spaces:@container spaces:flex spaces:flex-col spaces:flex-1 spaces:min-w-0 spaces:items-start spaces:md:items-end'>
			<div className='spaces:flex spaces:items-baseline spaces:whitespace-nowrap'>
				<span className='spaces:text-16/24 spaces:font-strong spaces:text-content-1'>{priceRange}</span>
				<span className='spaces:text-14/16 spaces:font-default spaces:text-content-3'>
					/mo{disclaimer}
				</span>
			</div>
			{(baseRent || leaseTerm) && (
				<div className='spaces:flex spaces:flex-col spaces:gap-4 spaces:md:items-end spaces:@pricing-detail:flex-row spaces:@pricing-detail:gap-8 spaces:text-14/16 spaces:font-default spaces:text-content-3'>
					{baseRent && <span className='spaces:whitespace-nowrap'>{baseRent} Base Rent</span>}
					{baseRent && leaseTerm && (
						<span className='spaces:hidden spaces:@pricing-detail:block spaces:self-center spaces:w-4 spaces:h-4 spaces:rounded-full spaces:bg-canvas-2 spaces:shrink-0' />
					)}
					{leaseTerm && pricing === 'flat' && <span className='spaces:whitespace-nowrap'>{leaseTerm}</span>}
					{leaseTerm &&
						pricing === 'revenue-management' &&
						(onLeaseTermClick ? (
							<Link
								onClick={e => {
									e.stopPropagation()
									onLeaseTermClick()
								}}
								label={leaseTerm}
								defaultUnderline={false}
								aria-label='Change lease term'
								role='button'
							/>
						) : (
							<span className='spaces:whitespace-nowrap'>{leaseTerm}</span>
						))}
				</div>
			)}
		</div>
	)
}

/* ---------- MatchingUnitsPanel ---------- */

interface MatchingUnitsPanelProps {
	units: UnitListItem[]
	/** Heading beside the unit count, e.g. "Matching Homes". */
	label?: string
	pricing?: UnitListButtonPricing
	hasFees?: boolean
	hasDisclaimerSymbol?: boolean
	onUnitClick?: (unitNumber: string) => void
	onPricingClick?: (unitNumber: string) => void
	onLeaseTermClick?: (unitNumber: string) => void
	className?: string
}

export function MatchingUnitsPanel ({
	units,
	label = 'Matching Units',
	pricing = 'flat',
	hasFees = true,
	hasDisclaimerSymbol = true,
	onUnitClick,
	onPricingClick,
	onLeaseTermClick,
	className = ''
}: MatchingUnitsPanelProps) {
	return (
		<div
			className={`spaces-matching-units spaces:bg-canvas-4 spaces:border spaces:border-canvas-3 spaces:rounded-16 spaces:shadow-sm-below spaces:overflow-hidden ${className}`}
		>
			<div className='spaces:flex spaces:items-center spaces:p-16'>
				<span className='spaces:text-16/24 spaces:font-default spaces:text-content-1'>
					<strong className='spaces:font-strong'>{units.length}</strong>{' '}
					<span className='spaces-matching-units-label spaces:text-content-3'>{label}</span>
				</span>
			</div>
			<div className='spaces:bg-canvas-base spaces:border-t spaces:border-canvas-3 spaces:rounded-t-16 spaces:flex spaces:flex-col spaces:gap-4 spaces:p-4 spaces:max-h-[448px] spaces:overflow-y-auto spaces:divide-y spaces:divide-canvas-3'>
				{units.map((unit, i) => (
					<div key={unit.unitNumber}>
						<UnitListButton
							{...unit}
							pricing={unit.pricing ?? pricing}
							hasFees={hasFees}
							hasDisclaimerSymbol={hasDisclaimerSymbol}

							onClick={() => onUnitClick?.(unit.unitNumber)}
							onPricingClick={
								onPricingClick ? () => onPricingClick(unit.unitNumber) : undefined
							}
							onLeaseTermClick={
								onLeaseTermClick ? () => onLeaseTermClick(unit.unitNumber) : undefined
							}
						/>
						<div className='spaces:h-4' />
					</div>
				))}
			</div>
		</div>
	)
}
